/**
 * Session — consomme GET /api/v1/me du BFF (sprint 2).
 *
 * MODE DÉVELOPPEMENT SANS BFF : tant que le BFF ne répond pas, un stub de
 * session est fourni UNIQUEMENT si import.meta.env.DEV — clairement affiché
 * dans la topbar (« session locale »). En production, pas de BFF = écran
 * de connexion, jamais un faux utilisateur.
 */
import type { Me } from "@geceram/shared";
import { MeSchema } from "@geceram/shared";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type SessionState =
  | { status: "loading" }
  | { status: "authenticated"; me: Me; devStub: boolean }
  | { status: "anonymous" };

const SessionContext = createContext<SessionState>({ status: "loading" });

const DEV_STUB: Me = {
  email: "dev@geceram.local",
  name: "Session locale (dev)",
  roles: ["admin"],
  permissions: [],
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/v1/me", { credentials: "include" });
        if (res.ok) {
          const parsed = MeSchema.safeParse(await res.json());
          if (parsed.success && !cancelled) {
            setState({ status: "authenticated", me: parsed.data, devStub: false });
            return;
          }
        }
        if (!cancelled) {
          if (import.meta.env.DEV) {
            setState({ status: "authenticated", me: DEV_STUB, devStub: true });
          } else {
            setState({ status: "anonymous" });
          }
        }
      } catch {
        if (!cancelled) {
          if (import.meta.env.DEV) {
            setState({ status: "authenticated", me: DEV_STUB, devStub: true });
          } else {
            setState({ status: "anonymous" });
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
